import { Injectable } from '@angular/core';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DelayedAction } from '../entities/actions/delayed-action.entity';

/**
 * A place to store certain actions that need to be executed at a later time when a certain condition
 * is fulfilled (e.g. wait until a resource is created to execute some additional calls)
 * The clear function should be used in the onDestroy methods of the root component where it's used
 * (Maybe also call it in the onInit for good measure)
 */
@Injectable({
    providedIn: 'root'
})
export class ShDelayedActionService {
    private delayedActions: DelayedAction[] = [];

    public clearDelayedActions(): void {
        this.delayedActions = [];
    }

    /**
     * Adds a delayed action with given id. If the id already exists, the old action will be replaced
     */
    public addDelayedAction(id: string, action$: Observable<any>): void {
        const existing: DelayedAction | undefined = this.delayedActions.find(action => action.id === id);
        if (existing) {
            existing.action$ = action$;
            return;
        }

        this.delayedActions.push({ id, action$ });
    }

    public removeDelayedAction(id: string): void {
        const i: number = this.delayedActions.findIndex(action => action.id === id);
        if (i >= 0) {
            this.delayedActions.splice(i, 1);
        }
    }

    public executeDelayedActions(): Observable<any[]> {
        return this.delayedActions.length > 0
            ? combineLatest(this.delayedActions.map(action => action.action$))
            : of([]);
    }

    /**
     * Executes the stored delayed actions and returns the source observable
     * Optionally executes a post action stream, only when any delayed actions have executed
     * @param postActionStream$ A stream factory that accepts an input of the same type as the unwrapped source observable. Executes after the delayed actions have finished.
     * @returns The source observable
     */
    public executeReturnInput<T>(
        postActionStream$?: (results: T) => Observable<any>
    ): (source: Observable<T>) => Observable<T> {
        return (source: Observable<T>) =>
            source.pipe(
                switchMap(input =>
                    this.executeDelayedActions().pipe(
                        switchMap(actionResults =>
                            actionResults.length > 0 && postActionStream$ ? postActionStream$(input) : of([])
                        ),
                        map(() => input)
                    )
                )
            );
    }
}
